import { cn } from '@/lib/utils';
import { GLOBAL_CALLING_CODES } from '@chiroup/core/constants/globals';
import { parsePhoneValue } from '@chiroup/core/functions/parsePhoneValue';
import { FormError } from '@chiroup/core/types/ErrorResponse.type';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';
import { ErrorIcon } from './ErrorIcon';
import { FieldErrors } from './FieldErrors';

type Props = {
  name: string;
  className?: string;
  label?: string;
  value?: string | null;
  onChange: (val: string) => void;
  errors?: FormError;
  disabled?: boolean;
  autoFocus?: boolean;
  international?: boolean;
  countryCode?: string;
};

// Helper functions to handle international phone number formatting
const getPhoneFormat = (countryCode: string): string => {
  if (!countryCode) return '###############';

  // Common country code formats (customize as needed)
  switch (countryCode) {
    case '+1': // US/Canada
      return '(###) ### ####';
    case '+44': // UK
      return '#### ######';
    case '+61': // Australia
      return '#### ######';
    case '+33': // France
      return '# ## ## ## ##';
    case '+49': // Germany
      return '### #######';
    case '+55': // Brazil
      return '(##) #### ####';
    case '+86': // China
      return '### #### ####';
    case '+91': // India
      return '##### #####';
    case '+81': // Japan
      return '## #### ####';
    case '+52': // Mexico
      return '## #### ####';
    case '+297': // Aruba
      return '### ####';
    default:
      // For other country codes, use a generic format based on expected length
      // Different country codes have different typical phone number lengths
      if (countryCode.length <= 3) {
        // Shorter country codes like +1
        return '### ### ####';
      } else if (countryCode.length <= 4) {
        // Medium length country codes
        return '#### ######';
      } else {
        // Longer country codes
        return '#### #####';
      }
  }
};

// Calculate padding based on country code length
const getCountryCodePadding = (countryCode: string): string => {
  if (!countryCode) return '7rem';

  // Base padding plus additional space for longer country codes
  const baseRem = 6.8;
  const additionalPerChar = 0.4; // rem per character
  const countryCodeLength = countryCode.length;

  return `${baseRem + (countryCodeLength - 2) * additionalPerChar}rem`;
};

export const Phone: React.FC<Props> = ({
  name,
  className = '',
  label,
  value,
  onChange,
  errors,
  disabled = false,
  autoFocus = false,
  international = true,
  countryCode = '+1',
}) => {
  const [number, setNumber] = useState<string>();
  const [phoneRegionCode, setPhoneRegionCode] = useState<string>(countryCode);

  useEffect(() => {
    if (value) {
      const { countryCode: cc, nationalNumber } = parsePhoneValue(value, countryCode);
      setPhoneRegionCode(cc);
      setNumber(nationalNumber);
    } else {
      setNumber('');
    }
  }, [countryCode, value]);

  const onChangeValue = (event: ChangeEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const cleanedVal = val.replace(/[ ()_]/g, '');

    if (phoneRegionCode && cleanedVal) {
      onChange(`${phoneRegionCode} ${cleanedVal}`);
    } else {
      onChange('');
    }
  };

  const onChangeCountry = (event: ChangeEvent<HTMLSelectElement>) => {
    const val = event.target.value;
    setPhoneRegionCode(val);
    const cleanedNumber = (number || '').replace(/[ _]/g, '');

    if (cleanedNumber) {
      onChange(`${val} ${cleanedNumber}`);
    } else {
      onChange('');
    }
  };

  return (
    <div className={cn(className, 'flex flex-col gap-1')} data-cy="phone">
      {!!label && (
        <div>
          <label
            htmlFor={name}
            className="block text-sm font-medium leading-5 text-gray-900  dark:text-dark-gray-200"
          >
            {label}
          </label>
        </div>
      )}
      <div>
        <div className="relative rounded-md">
          {international && (
            <div className="absolute inset-y-0 left-0 flex items-center">
              <select
                aria-label="Country"
                className={[
                  'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
                  'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',
                  errors
                    ? 'border-red-500 text-red-900 placeholder-red-500 focus:border-red-500 focus:ring-red'
                    : 'focus:border-primary-500 focus:ring-primary-500 outline-none',
                  disabled
                    ? 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed'
                    : '',
                ].join(' ')}
                onChange={onChangeCountry}
                value={phoneRegionCode}
                disabled={disabled}
              >
                {GLOBAL_CALLING_CODES.map(code => (
                  <option value={code} key={code}>
                    {code}
                  </option>
                ))}
                <option value="">Other</option>
              </select>
            </div>
          )}
          <PatternFormat
            format={getPhoneFormat(phoneRegionCode)}
            allowEmptyFormatting
            mask="_"
            value={number}
            onChange={onChangeValue}
            key={phoneRegionCode}
            className={[
              'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground border-input flex h-9 w-full min-w-0 rounded-md border px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm bg-white dark:bg-dark-gray-700',
              'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] focus-visible:border-primary-500 focus-visible:ring-primary-500',
              errors
                ? 'border-red-500 text-red-900 placeholder-red-500 focus:border-red-500 focus:ring-red'
                : 'focus:border-primary-500 focus:ring-primary-500 outline-none',
              disabled
                ? 'text-gray-500 dark:text-dark-gray-300 bg-gray-300 dark:bg-dark-gray-500 cursor-not-allowed'
                : '',
            ].join(' ')}
            style={!international ? {} : { paddingLeft: getCountryCodePadding(phoneRegionCode) }}
            disabled={disabled}
            autoFocus={autoFocus}
            type="tel"
            inputMode="numeric"
          />

          {errors && <ErrorIcon />}
        </div>
        <FieldErrors errors={errors} />
      </div>
    </div>
  );
};
